/* eslint-disable react/destructuring-assignment */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators, compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { HotKeys } from 'react-hotkeys';
import { withLastLocation } from 'react-router-last-location';
import _emitter, { C_RULES_CREATE_NEW_INTENT } from '_emitter';
import _isEmpty from 'lodash/isEmpty';
import Preloader from '@omilia/preloader';
import Tooltip from '@omilia/tooltip';

import {
  getProject,
  getProjectIsLoading,
} from 'store/services/project/selectors';
import * as actions from 'store/services/testSets/actions';
import * as selectors from 'store/services/testSets/selectors';
import * as dictionariesActions from 'store/services/dictionaries/actions';
import * as dictionariesSelectors from 'store/services/dictionaries/selectors';
import * as entriesActions from 'store/services/testSetEntries/actions';
import * as entriesSelectors from 'store/services/testSetEntries/selectors';
import * as dictionaryEntriesActions from 'store/services/dictionaryEntries/actions';
import * as dictionaryEntriesSelectors from 'store/services/dictionaryEntries/selectors';
import * as intentsActions from 'store/services/intents/actions';
import * as intentsSelectors from 'store/services/intents/selectors';
import * as annotationsSelectors from 'store/services/annotations/selectors';
import * as annotationsActions from 'store/services/annotations/actions';
import {
  getContextsAll as getContextsAllSelector,
  getSelectedContextFromAll,
  getHasSelectedContextFromAll,
} from 'store/services/contexts/selectors';
import {
  getContextsAll as getContextsAllAction,
  selectContextFromAll,
  deselectContextFromAll,
} from 'store/services/contexts/actions';

import {
  testSetType,
  projectType,
  intentType,
  dictionaryType,
  testSetEntryType,
  annotationType,
} from 'types';

import Pagination from 'components/Pagination';
import Button from 'components/Button';
import TestSetsHeader from './components/TestSetsHeader';
import NoTestSets from './components/NoTestSets';
import NoSelectedTestSet from './components/NoSelectedTestSet';
import AddTestSetInput from './components/AddTestSetInput';
import TestSetsList from './components/TestSetsList';

import entriesWithAnnotations from './utils';
import './style.scss';

const HOTKEYS_MAP = {
  run: 'alt+r',
};

export class TestSets extends Component {
  static propTypes = {
    annotations: PropTypes.arrayOf(annotationType).isRequired,
    count: PropTypes.number.isRequired,
    dictionaries: PropTypes.arrayOf(dictionaryType).isRequired,
    entries: PropTypes.arrayOf(testSetEntryType).isRequired,
    executeAnnotator: PropTypes.func.isRequired,
    hasAnnotations: PropTypes.bool.isRequired,
    hasSelectedTestSet: PropTypes.bool.isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    intents: PropTypes.arrayOf(intentType).isRequired,
    isAdding: PropTypes.bool.isRequired,
    isAddingDictionary: PropTypes.bool.isRequired,
    isAnnotating: PropTypes.bool.isRequired,
    isDeleting: PropTypes.bool.isRequired,
    isLoading: PropTypes.bool.isRequired,

    isLoadingAllIntents: PropTypes.bool.isRequired,
    isLoadingDictionaries: PropTypes.bool.isRequired,
    isLoadingEntries: PropTypes.bool.isRequired,
    isUpdating: PropTypes.bool.isRequired,
    location: PropTypes.shape({
      pathname: PropTypes.string.isRequired,
    }).isRequired,

    match: PropTypes.shape({
      params: PropTypes.shape({
        company: PropTypes.string.isRequired,
        project: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    paginationOptions: PropTypes.shape({
      page: PropTypes.number,
      value__contains: PropTypes.string,
    }).isRequired,
    project: projectType.isRequired,
    projectIsLoading: PropTypes.bool.isRequired,
    resetAnnotator: PropTypes.func.isRequired,
    selectedTestSet: testSetType.isRequired,
    testSets: PropTypes.arrayOf(testSetType).isRequired,

    onAddDictionaryEntry: PropTypes.func.isRequired,
    onAddTestSetEntry: PropTypes.func.isRequired,
    onAddTestSetEntryAfter: PropTypes.func.isRequired,
    onAddTestSetEntryBefore: PropTypes.func.isRequired,
    onDeleteTestSetEntry: PropTypes.func.isRequired,

    onDeselectAnnotatorResult: PropTypes.func.isRequired,
    onDeselectTestSet: PropTypes.func.isRequired,
    onGetAllDictionaries: PropTypes.func.isRequired,
    onGetAllIntents: PropTypes.func.isRequired,
    onGetIntent: PropTypes.func.isRequired,

    onGetTestSetEntries: PropTypes.func.isRequired,
    onGetTestSets: PropTypes.func.isRequired,
    onSelectAnnotatorResultByID: PropTypes.func.isRequired,
    onUpdateIntent: PropTypes.func.isRequired,
    onUpdateTestSetEntry: PropTypes.func.isRequired,
    // Last location from withLastLocation
    lastLocation: PropTypes.shape({ pathname: PropTypes.string.isRequired }),
  };

  state = {
    isActiveList: false,
  };

  handleHotKeys = {
    run: this.props.executeAnnotator,
  };

  componentDidMount() {
    this.props.onGetTestSets();
  }

  componentWillUnmount() {
    if (this.props.hasAnnotations) {
      this.props.resetAnnotator();
    }
  }

  getEntries = () =>
    entriesWithAnnotations(this.props.entries, this.props.annotations);

  handleOpenNewIntentModal = constraintSet => {
    _emitter.emit(C_RULES_CREATE_NEW_INTENT, {
      constraintSets: [constraintSet],
    });
  };

  handleGoToIntent = id => {
    const {
      history,
      match: { params },
    } = this.props;

    history.push(`/${params.company}/${params.project}/intents/${id}`);
  };

  handleOpenIntentAndFeatureView = id => {
    this.props.onSelectAnnotatorResultByID(id);
  };

  handleCloseIntentAndFeatureView = () => {
    this.props.onDeselectAnnotatorResult();
  };

  handleLoadEntries = params => {
    this.props.resetAnnotator();
    this.props.onGetTestSetEntries(params);
  };

  handleToggleTestSetsList = isActiveList => {
    this.setState({ isActiveList });
  };

  handleAutoAddTestSetEntry = () => {
    this.props.onAddTestSetEntry({
      order: 0,
      value: this.props.paginationOptions.value__contains,
    });
  };

  renderNoTerms(hasSelectedTestSet, isLoading, value, len) {
    if (hasSelectedTestSet && !isLoading && value && !len) {
      return (
        <div styleName="no-terms">
          Term
          <Tooltip
            content={
              <React.Fragment>
                Add this
                <br /> token in
                <br /> the test
                <br /> set
              </React.Fragment>
            }
          >
            <Button
              isLink
              data-test="auto-add"
              onClick={this.handleAutoAddTestSetEntry}
            >
              {`"${value}"`}
            </Button>
          </Tooltip>
          does not exist at this test set.
        </div>
      );
    }
    return null;
  }

  renderList() {
    const {
      onDeleteTestSetEntry,
      onUpdateTestSetEntry,
      onAddTestSetEntry,
      hasSelectedTestSet,
      onAddTestSetEntryBefore,
      onAddTestSetEntryAfter,
      dictionaries,
      onGetAllDictionaries,
      isLoadingDictionaries,
      onAddDictionaryEntry,
      isAddingDictionary,
      onGetAllIntents,
      intents,
      isLoadingAllIntents,
      onUpdateIntent,
      onGetIntent,
    } = this.props;

    if (hasSelectedTestSet) {
      return (
        <div styleName="list">
          <AddTestSetInput
            addEntry={onAddTestSetEntry}
            isLoading={this.props.isAdding}
          />
          <TestSetsList
            closeIntentAndFeatureView={this.handleCloseIntentAndFeatureView}
            dictionaries={dictionaries}
            entries={this.getEntries()}
            intents={intents}
            isAddingDictionary={isAddingDictionary}
            isLoadingAllIntents={isLoadingAllIntents}
            // Dictionaries
            isLoadingDictionaries={isLoadingDictionaries}
            openIntentAndFeatureView={this.handleOpenIntentAndFeatureView}
            onAddDictionaryEntry={onAddDictionaryEntry}
            onAddEntry={onAddTestSetEntry}
            onAddEntryAfter={onAddTestSetEntryAfter}
            // Intents
            onAddEntryBefore={onAddTestSetEntryBefore}
            onDeleteEntry={onDeleteTestSetEntry}
            onGetAllIntents={onGetAllIntents}
            onGetDictionaries={onGetAllDictionaries}
            onGetIntent={onGetIntent}
            onGoToIntent={this.handleGoToIntent}
            onOpenNewIntentModal={this.handleOpenNewIntentModal}
            onUpdateEntry={onUpdateTestSetEntry}
            onUpdateIntent={onUpdateIntent}
          />
        </div>
      );
    }
    return null;
  }

  renderNoTestSets = (isLoading, projectIsLoading, testSets, project) => {
    if (
      !testSets.length &&
      !isLoading &&
      !projectIsLoading &&
      !_isEmpty(project)
    ) {
      return <NoTestSets project={project} />;
    }
    return null;
  };

  renderNoSelectedTestSet(
    isLoading,
    projectIsLoading,
    testSets,
    project,
    hasSelectedTestSet,
  ) {
    if (
      testSets.length > 0 &&
      !isLoading &&
      !projectIsLoading &&
      !hasSelectedTestSet
    ) {
      return (
        <NoSelectedTestSet toggleTestSetsList={this.handleToggleTestSetsList} />
      );
    }

    return null;
  }

  renderPreloader = () => {
    const {
      isLoading,
      projectIsLoading,
      isLoadingEntries,
      isUpdating,
      isDeleting,
      isAdding,
      isAnnotating,
    } = this.props;

    if (
      isLoading ||
      projectIsLoading ||
      isLoadingEntries ||
      isUpdating ||
      isDeleting ||
      isAdding ||
      isAnnotating
    ) {
      return <Preloader showBackdrop />;
    }

    return null;
  };

  render() {
    const {
      testSets,
      isLoading,
      project,
      projectIsLoading,
      hasSelectedTestSet,
      selectedTestSet,
      onDeselectTestSet,
      executeAnnotator,
      entries,
      paginationOptions,
      isLoadingEntries,
      count,
    } = this.props;

    const { isActiveList } = this.state;
    return (
      <HotKeys
        handlers={this.handleHotKeys}
        keyMap={HOTKEYS_MAP}
        styleName="test__sets"
      >
        <TestSetsHeader
          executeAnnotator={executeAnnotator}
          hasSelectedTestSet={hasSelectedTestSet}
          isActive={isActiveList}
          selectedTestSet={selectedTestSet}
          testSets={testSets}
          toggleTestSetsList={this.handleToggleTestSetsList}
          onDeselectTestSet={onDeselectTestSet}
        />
        <div styleName="wrap">
          {this.renderNoTestSets(
            isLoading,
            projectIsLoading,
            testSets,
            project,
          )}
          {this.renderNoSelectedTestSet(
            isLoading,
            projectIsLoading,
            testSets,
            project,
            hasSelectedTestSet,
          )}
          {this.renderPreloader()}
          {this.renderNoTerms(
            hasSelectedTestSet,
            isLoadingEntries,
            paginationOptions.value__contains,
            entries.length,
          )}
          {this.renderList()}
          {entries.length > 0 && (
            <Pagination
              className="footer__pagination"
              count={count}
              getItems={this.handleLoadEntries}
              paginationOptions={paginationOptions}
            />
          )}
        </div>
      </HotKeys>
    );
  }
}

const mapStateToProps = state => ({
  project: getProject(state),
  projectIsLoading: getProjectIsLoading(state),
  isLoading: selectors.getIsLoadingList(state),
  testSets: selectors.getTestSetsList(state),
  selectedTestSet: selectors.getTestSet(state),
  hasSelectedTestSet: selectors.getHasSelectedTestSet(state),
  contexts: getContextsAllSelector(state),
  currentContext: getSelectedContextFromAll(state),
  hasSelectedContext: getHasSelectedContextFromAll(state),

  dictionaries: dictionariesSelectors.getAllDictionaries(state),
  isLoadingDictionaries: dictionariesSelectors.getIsLoadingAll(state),
  entries: entriesSelectors.getTestSetEntriesList(state),
  isLoadingEntries: entriesSelectors.getIsLoadingList(state),
  paginationOptions: entriesSelectors.getPaginationOptions(state),
  count: entriesSelectors.getTestSetEntriesListCount(state),
  isAdding: entriesSelectors.getIsAdding(state),
  isUpdating: entriesSelectors.getIsUpdating(state),
  isDeleting: entriesSelectors.getIsDeleting(state),
  annotations: annotationsSelectors.getAnnotations(state),
  isAnnotating: annotationsSelectors.getIsLoading(state),
  hasAnnotations: annotationsSelectors.getHasAnnotations(state),
  isAddingDictionary: dictionaryEntriesSelectors.getIsAdding(state),
  intents: intentsSelectors.getAllIntents(state),
  isLoadingAllIntents: intentsSelectors.getIsLoadingAll(state),
});

const mapDispatchToProps = dispatch => ({
  onGetTestSets: bindActionCreators(actions.getTestSetsList, dispatch),
  onDeselectTestSet: bindActionCreators(actions.deselectTestSet, dispatch),
  onGetContexts: bindActionCreators(getContextsAllAction, dispatch),
  onSelectContext: bindActionCreators(selectContextFromAll, dispatch),
  onDeselectContext: bindActionCreators(deselectContextFromAll, dispatch),

  onGetAllDictionaries: bindActionCreators(
    dictionariesActions.getAllDictionaries,
    dispatch,
  ),
  onGetTestSetEntries: bindActionCreators(
    entriesActions.getTestSetEntriesList,
    dispatch,
  ),
  onDeleteTestSetEntry: bindActionCreators(
    entriesActions.deleteTestSetEntry,
    dispatch,
  ),
  onUpdateTestSetEntry: bindActionCreators(
    entriesActions.updateTestSetEntry,
    dispatch,
  ),
  onAddTestSetEntry: bindActionCreators(
    entriesActions.addTestSetEntry,
    dispatch,
  ),
  onAddTestSetEntryBefore: bindActionCreators(
    entriesActions.addTestSetEntryBefore,
    dispatch,
  ),
  onAddTestSetEntryAfter: bindActionCreators(
    entriesActions.addTestSetEntryAfter,
    dispatch,
  ),
  onAddDictionaryEntry: bindActionCreators(
    dictionaryEntriesActions.addDictionaryEntry,
    dispatch,
  ),
  onGetAllIntents: bindActionCreators(intentsActions.getAllIntents, dispatch),
  onUpdateIntent: bindActionCreators(intentsActions.updateIntent, dispatch),
  onGetIntent: bindActionCreators(intentsActions.getIntent, dispatch),
  onSelectAnnotatorResultByID: bindActionCreators(
    annotationsActions.selectAnnotatorResultByID,
    dispatch,
  ),
  onDeselectAnnotatorResult: bindActionCreators(
    annotationsActions.deselectAnnotatorResult,
    dispatch,
  ),
  executeAnnotator: bindActionCreators(annotationsActions.annotate, dispatch),
  resetAnnotator: bindActionCreators(annotationsActions.reset, dispatch),
});

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withLastLocation,
  withRouter,
)(TestSets);
